import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import { Screen } from "./types";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Analytics } from "@vercel/analytics/react";

// All public screens are imported eagerly — the SSR prerender uses
// renderToString() which cannot render React.lazy() components. Lazy-loading
// screens causes empty prerendered HTML and an infinite spinner on Vercel.
import HomeScreen from "./screens/HomeScreen";
import AboutScreen from "./screens/AboutScreen";
import ServicesScreen from "./screens/ServicesScreen";
import CoursesScreen from "./screens/CoursesScreen";
import ProgrammesScreen from "./screens/ProgrammesScreen";
import DirectoryScreen from "./screens/DirectoryScreen";
import ContactScreen from "./screens/ContactScreen";
import CaseStudiesScreen from "./screens/CaseStudiesScreen";
import NotFoundScreen from "./screens/NotFoundScreen";

// AdminScreen is safe to lazy-load: it is never prerendered (no /admin route
// in the prerender script) and is only accessed client-side by admins.
const AdminScreen = React.lazy(() => import("./screens/AdminScreen"));



// Search redirect: ?s=query → matching route
const routeMap: { keywords: string[]; path: string }[] = [
  {
    keywords: ["contact", "reach", "email", "phone", "touch"],
    path: "/contact",
  },
  { keywords: ["about", "who", "company", "story", "our"], path: "/about" },
  {
    keywords: [
      "services",
      "cctv",
      "web",
      "mobile",
      "voip",
      "cybersecurity",
      "security",
      "it",
      "consultancy",
      "data",
      "analytics",
      "surveillance",
      "iot",
      "cloud",
    ],
    path: "/services",
  },
  {
    keywords: [
      "courses",
      "training",
      "learn",
      "education",
      "digital",
      "school",
      "price",
      "fees",
    ],
    path: "/courses",
  },
  {
    keywords: [
      "programmes",
      "programs",
      "siwes",
      "nysc",
      "apply",
      "placement",
      "internship",
    ],
    path: "/programmes",
  },
  {
    keywords: ["team", "directory", "people", "staff", "members", "founders"],
    path: "/team",
  },
  {
    keywords: ["case", "studies", "portfolio", "projects", "work", "cases"],
    path: "/case-studies",
  },
  { keywords: ["home", "innovatech", "main", "index"], path: "/" },
];

const SearchRedirect: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const query = params.get("s");
    if (!query) return;

    const q = query.toLowerCase().trim();
    const match = routeMap.find(({ keywords }) =>
      keywords.some((kw) => q.includes(kw)),
    );

    // Navigate to matched route, stripping the ?s= param
    navigate(match ? match.path : "/", { replace: true });
  }, [search, navigate]);

  return null;
};

const AppContent: React.FC = () => {
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [isMounted, setIsMounted] = useState(false);

  // Load saved theme on client mount to prevent SSR hydration mismatch
  useEffect(() => {
    const migrated = localStorage.getItem("theme_migrated_v2");
    if (!migrated) {
      localStorage.setItem("theme", "light");
      localStorage.setItem("theme_migrated_v2", "true");
      setTheme("light");
    } else {
      const saved = localStorage.getItem("theme");
      if (saved === "light" || saved === "dark") {
        setTheme(saved);
      }
    }
    setIsMounted(true);
  }, []);

  // Log real web traffic / unique visits
  useEffect(() => {
    let visitorId = localStorage.getItem("visitor_id");
    if (!visitorId) {
      visitorId = typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem("visitor_id", visitorId);
    }

    fetch("/api/log-visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId }),
    }).catch((err) => console.warn("Failed to log visit:", err));
  }, []);


  // Handle Theme Change
  useEffect(() => {
    if (!isMounted) return;
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme, isMounted]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Initialize GSAP smooth scrolling
  useSmoothScroll();

  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <div className="flex flex-col min-h-screen transition-colors duration-500">
      {!isAdmin && <Navbar isDarkMode={theme === "dark"} onToggleTheme={toggleTheme} />}
      <SearchRedirect />
      <main className={`flex-grow w-full ${isAdmin ? "" : "pt-24 pb-12"}`}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/about" element={<AboutScreen />} />
          <Route path="/services" element={<ServicesScreen />} />
          <Route path="/courses" element={<CoursesScreen />} />
          <Route path="/programmes" element={<ProgrammesScreen />} />
          <Route path="/team" element={<DirectoryScreen />} />
          <Route path="/contact" element={<ContactScreen />} />
          <Route path="/case-studies" element={<CaseStudiesScreen />} />
          <Route
            path="/admin/*"
            element={
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center min-h-screen bg-canvas dark:bg-canvas-dark">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                }
              >
                <AdminScreen isDarkMode={theme === "dark"} onToggleTheme={toggleTheme} />
              </React.Suspense>
            }
          />
          {/* Fallback route */}
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </main>
      {!isAdmin && <Footer isDarkMode={theme === "dark"} />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <>
      <AppContent />
      <Analytics />
    </>
  );
};

export default App;
